import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Form, Row, Col, InputGroup, Dropdown, Spinner, Modal } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { adminService } from '../../services/adminService';
import DateRangeFilter, { PeriodValue } from '../../components/Common/DateRangeFilter';
import toast from 'react-hot-toast';

const Cinemas: React.FC = () => {
  const [cinemas, setCinemas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCity, setFilterCity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedCinema, setSelectedCinema] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<any>({
    name: '',
    location: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    status: 'Active',
    screens: 1,
    totalSeats: 100,
    amenities: '',
    vistaCinemaId: '',
    latitude: '',
    longitude: '',
  });
  const [period, setPeriod] = useState<PeriodValue>({ period: 'this_week' });

  useEffect(() => {
    loadCinemas();
  }, [currentPage, filterCity, filterStatus, period]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        loadCinemas();
      } else {
        setCurrentPage(1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadCinemas = async () => {
    try {
      setLoading(true);
      const dateRange =
        period.period === 'custom' && period.dateFrom && period.dateTo
          ? { dateFrom: period.dateFrom, dateTo: period.dateTo }
          : null;
      const response = await adminService.getCinemas({
        search: searchTerm,
        city: filterCity === 'all' ? undefined : filterCity,
        status: filterStatus === 'all' ? undefined : filterStatus,
        page: currentPage,
        limit: itemsPerPage,
        sortBy: 'name',
        sortOrder: 'asc',
        period: dateRange ? 'custom' : period.period,
        dateFrom: dateRange?.dateFrom,
        dateTo: dateRange?.dateTo,
      });

      if (response.success && response.data) {
        setCinemas(response.data);
      }
    } catch (error: any) {
      console.error('Error loading cinemas:', error);
      toast.error('Failed to load cinemas');
      setCinemas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCinema = async (cinema: any) => {
    if (!window.confirm(`Are you sure you want to delete cinema "${cinema.name}"?`)) {
      return;
    }

    try {
      const response = await adminService.deleteCinema(cinema.id);
      if (response.success) {
        toast.success(`Cinema "${cinema.name}" deleted successfully!`);
        loadCinemas();
      }
    } catch (error: any) {
      toast.error('Failed to delete cinema');
    }
  };
  
  const handleOpenAdd = () => {
    setModalMode('add');
    setSelectedCinema(null);
    setFormData({
      name: '',
      location: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      phone: '',
      email: '',
      status: 'Active',
      screens: 1,
      totalSeats: 100,
      amenities: '',
      vistaCinemaId: '',
      latitude: '',
      longitude: '',
    });
    setShowModal(true);
  };

  const handleOpenEdit = (cinema: any) => {
    setModalMode('edit');
    setSelectedCinema(cinema);
    setFormData({
      name: cinema.name || '',
      location: cinema.location || '',
      address: cinema.address || '',
      city: cinema.city || '',
      state: cinema.state || '',
      pincode: cinema.pincode || '',
      phone: cinema.phone || '',
      email: cinema.email || '',
      status: cinema.status || 'Active',
      screens: cinema.screens || 1,
      totalSeats: cinema.totalSeats || 100,
      amenities: Array.isArray(cinema.amenities) ? cinema.amenities.join(', ') : (cinema.amenities || ''),
      vistaCinemaId: cinema.vistaCinemaId || cinema.vistacinemaid || '',
      latitude: cinema.latitude !== null && cinema.latitude !== undefined ? String(cinema.latitude) : '',
      longitude: cinema.longitude !== null && cinema.longitude !== undefined ? String(cinema.longitude) : '',
    });
    setShowModal(true);
  };

  const handleOpenDetails = (cinema: any) => {
    setSelectedCinema(cinema);
    setShowDetailsModal(true);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const buildPayload = () => {
    const payload: any = {
      name: formData.name?.trim(),
      location: formData.location?.trim(),
      address: formData.address?.trim(),
      city: formData.city?.trim(),
      state: formData.state?.trim(),
      pincode: formData.pincode?.trim(),
      phone: formData.phone?.trim(),
      email: formData.email?.trim(),
      status: formData.status,
      screens: Number(formData.screens) || 0,
      totalSeats: Number(formData.totalSeats) || 0,
      vistaCinemaId: formData.vistaCinemaId?.trim() || undefined,
    };

    // Add latitude and longitude if provided
    if (formData.latitude && formData.latitude.trim()) {
      const lat = parseFloat(formData.latitude.trim());
      if (!Number.isNaN(lat) && lat >= -90 && lat <= 90) {
        payload.latitude = lat;
      }
    }
    if (formData.longitude && formData.longitude.trim()) {
      const lng = parseFloat(formData.longitude.trim());
      if (!Number.isNaN(lng) && lng >= -180 && lng <= 180) {
        payload.longitude = lng;
      }
    }

    const amenities = formData.amenities;
    if (amenities) {
      payload.amenities = amenities
        .split(',')
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);
    } else {
      payload.amenities = [];
    }

    return payload;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = buildPayload();

      if (!payload.name?.trim()) {
        toast.error('Cinema name is required');
        setSaving(false);
        return;
      }
  
      if (!payload.location?.trim()) {
        toast.error('Location / Landmark is required');
        setSaving(false);
        return;
      }

      if (modalMode === 'edit' && selectedCinema) {
        const response = await adminService.updateCinema(selectedCinema.id, payload);
        if (response.success) {
          toast.success(`Cinema "${payload.name}" updated successfully!`);
        }
      } else {
        const response = await adminService.createCinema(payload);
        if (response.success) {
          toast.success(`Cinema "${payload.name}" created successfully!`);
        }
      }

      setShowModal(false);
      await loadCinemas();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save cinema');
    } finally {
      setSaving(false);
    }
  };
  
  const filteredCinemas = cinemas;

  const getStatusBadge = (status: string) => {
    return <Badge bg={status === 'Active' ? 'success' : 'secondary'}>{status}</Badge>;
  };

  return (
    <>
      <Helmet>
        <title>Cinemas - MyCinemas Admin Panel</title>
      </Helmet>

      <div className="fade-in">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2 className="mb-1">Cinemas</h2>
            <p className="text-muted mb-0">Manage cinema locations and screens</p>
          </div>
          <Button variant="primary" onClick={handleOpenAdd}>
            <i className="bi-plus-circle me-1"></i>
            Add Cinema
          </Button>
        </div>

        <Card>
          <Card.Header>
            <Row className="cinemas-card-header-row">
              <Col xs="auto" className="cinemas-title-col">
                <h5 className="mb-0">All Cinemas ({filteredCinemas.length})</h5>
              </Col>
              <Col className="cinemas-search-col">
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search cinemas..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col xs="auto" className="cinemas-filter-col">
                {/* <DateRangeFilter value={period} onChange={setPeriod} /> */}
              </Col>
            </Row>
          </Card.Header>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Cinema</th>
                  <th>Location</th>
                  <th>Phone</th>
                  <th>Screens</th>
                  <th>Total Seats</th>
                  <th>Status</th>
                  <th>Bookings</th>
                  <th>
                    <div className="d-flex flex-column">
                      <span>Revenue</span>
                      <span className="period-chip">
                        <i className="bi-calendar3"></i>
                        {period.period === 'all'
                          ? 'Overall'
                          : period.period === 'today'
                            ? 'Today'
                            : period.period === 'this_week'
                              ? 'This Week'
                              : period.period === 'this_month'
                                ? 'This Month'
                                : period.period === 'this_year'
                                  ? 'This Year'
                                  : 'Custom'}
                      </span>
                    </div>
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCinemas.map((cinema) => (
                  <tr key={cinema.id}>
                    <td>
                      <div>
                        <div className="fw-medium">{cinema.name}</div>
                        <small className="text-muted">{cinema.address}</small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <div className="fw-medium">
                          {cinema.location ? cinema.location : `${cinema.city}, ${cinema.state}`}
                        </div>
                        {cinema.email && (
                          <small className="text-muted">{cinema.email}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      {cinema.phone ? (
                        <a
                          href={`tel:${cinema.phone}`}
                          className="fw-semibold text-decoration-none d-inline-flex align-items-center"
                          style={{ color: "#198754", fontSize: "0.9rem" }}
                        >
                          <i
                            className="bi-telephone-fill me-1"
                            style={{ fontSize: "0.85rem" }}
                          ></i>
                          {cinema.phone}
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-center">
                      <div className="fw-medium">{cinema.screens}</div>
                    </td>
                    <td className="text-center">
                      <div className="fw-medium">{cinema.totalSeats}</div>
                    </td>
                    <td>{getStatusBadge(cinema.status)}</td>
                    <td className="text-center">
                      <div className="fw-medium">
                        {Number(cinema.periodBookings ?? cinema.totalBookings ?? 0)}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="fw-bold" style={{ fontSize: "1rem" }}>
                        ₹{Number(
                          period.period === 'all'
                            ? (cinema.revenue || 0)
                            : (cinema.periodRevenue ?? 0)
                        ).toLocaleString('en-IN')}
                      </div>
                      {cinema.revenueChangePct != null && (
                        <span
                          className={`metric-delta ${
                            cinema.revenueChangePct > 0
                              ? 'positive'
                              : cinema.revenueChangePct < 0
                                ? 'negative'
                                : 'neutral'
                          }`}
                        >
                          <i
                            className={`bi-arrow-${
                              cinema.revenueChangePct > 0
                                ? 'up'
                                : cinema.revenueChangePct < 0
                                  ? 'down'
                                  : 'right'
                            }`}
                          ></i>
                          {cinema.revenueChangePct >= 0 ? '+' : ''}
                          {cinema.revenueChangePct}%
                        </span>
                      )}
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" id={`dropdown-${cinema.id}`}>
                          <i className="bi-three-dots"></i>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleOpenDetails(cinema)}>
                            <i className="bi-eye me-2"></i>View Details
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleOpenEdit(cinema)}>
                            <i className="bi-pencil me-2"></i>Edit Cinema
                          </Dropdown.Item>
                          {/* <Dropdown.Item>
                            <i className="bi-display me-2"></i>Manage Screens
                          </Dropdown.Item> */}
                          <Dropdown.Divider />
                          <Dropdown.Item className="text-danger" onClick={() => handleDeleteCinema(cinema)}>
                            <i className="bi-trash me-2"></i>Delete Cinema
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Header closeButton>
            <Modal.Title>{modalMode === 'edit' ? 'Edit Cinema' : 'Add Cinema'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="cinemaName">
                  Name <span className="text-danger">*</span>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      placeholder="Cinema name"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="cinemaLocation">
                  Location / Landmark <span className="text-danger">*</span>
                    <Form.Control
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      placeholder="Eg. Lulu Mall, Kochi"
                    />
                    <Form.Text className="text-muted">
                      Optional landmark or mall name shown publicly.
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="cinemaStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={formData.status}
                      onChange={(e) => handleInputChange('status', e.target.value)}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Maintenance">Maintenance</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="vistaCinemaId">
                    <Form.Label>Vista Cinema ID</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.vistaCinemaId}
                      onChange={(e) => handleInputChange('vistaCinemaId', e.target.value)}
                      placeholder="External Vista cinema identifier"
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="cinemaAddress">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={formData.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      placeholder="Full address"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="cinemaCity">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="City"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="cinemaState">
                    <Form.Label>State</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      placeholder="State"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="cinemaPincode">
                    <Form.Label>Pincode</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.pincode}
                      onChange={(e) => handleInputChange('pincode', e.target.value)}
                      placeholder="Pincode"
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="cinemaLatitude">
                    <Form.Label>Latitude</Form.Label>
                    <Form.Control
                      type="number"
                      step="any"
                      value={formData.latitude}
                      onChange={(e) => handleInputChange('latitude', e.target.value)}
                      placeholder="e.g., 9.9312"
                      min="-90"
                      max="90"
                    />
                    <Form.Text className="text-muted">
                      Required for distance-based filtering (range: -90 to 90)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="cinemaLongitude">
                    <Form.Label>Longitude</Form.Label>
                    <Form.Control
                      type="number"
                      step="any"
                      value={formData.longitude}
                      onChange={(e) => handleInputChange('longitude', e.target.value)}
                      placeholder="e.g., 76.2673"
                      min="-180"
                      max="180"
                    />
                    <Form.Text className="text-muted">
                      Required for distance-based filtering (range: -180 to 180)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="cinemaPhone">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="Contact number"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="cinemaEmail">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="Contact email"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="cinemaScreens">
                    <Form.Label>Screens</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={formData.screens}
                      onChange={(e) => handleInputChange('screens', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="cinemaSeats">
                    <Form.Label>Total Seats</Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={formData.totalSeats}
                      onChange={(e) => handleInputChange('totalSeats', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="cinemaAmenities">
                    <Form.Label>Amenities</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.amenities}
                      onChange={(e) => handleInputChange('amenities', e.target.value)}
                      placeholder="Comma separated amenities"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
        {/* View Details Modal - Beautifully Styled */}
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered className="details-modal">
          <Modal.Header closeButton className="border-0 pb-0">
            <div className="w-100">
              <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
                <i className="bi-info-circle-fill text-primary"></i>
                Cinema Details
              </Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body className="pt-4">
            {selectedCinema && (
              <div>
                {/* Header Section with Cinema Name and Status */}
                <div className="mb-4 pb-3 border-bottom">
                  <div className="d-flex justify-content-between align-items-start gap-3">
                    <div className="flex-grow-1">
                      <h4 className="mb-2 fw-bold text-dark">{selectedCinema.name}</h4>
                      <p className="text-muted mb-0 d-flex align-items-center gap-2">
                        <i className="bi-geo-alt"></i>
                        {selectedCinema.location || `${selectedCinema.city}, ${selectedCinema.state}`}
                      </p>
                    </div>
                    <div>
                      {getStatusBadge(selectedCinema.status)}
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="mb-4">
                  <h6 className="text-uppercase fw-bold text-secondary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <i className="bi-telephone me-2"></i>Contact Information
                  </h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">Phone</small>
                        <p className="mb-0 fw-medium">{selectedCinema.phone || <span className="text-secondary">—</span>}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">Email</small>
                        <p className="mb-0 fw-medium">{selectedCinema.email || <span className="text-secondary">—</span>}</p>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Location Details Section */}
                <div className="mb-4">
                  <h6 className="text-uppercase fw-bold text-secondary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <i className="bi-map me-2"></i>Location Details
                  </h6>
                  <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px', marginBottom: '12px' }}>
                    <small className="text-muted d-block mb-1">Address</small>
                    <p className="mb-0 fw-medium">{selectedCinema.address || <span className="text-secondary">—</span>}</p>
                  </div>
                  <Row className="g-3">
                    <Col md={4}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">City</small>
                        <p className="mb-0 fw-medium">{selectedCinema.city || <span className="text-secondary">—</span>}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">State</small>
                        <p className="mb-0 fw-medium">{selectedCinema.state || <span className="text-secondary">—</span>}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">Pincode</small>
                        <p className="mb-0 fw-medium">{selectedCinema.pincode || <span className="text-secondary">—</span>}</p>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Coordinates Section */}
                <div className="mb-4">
                  <h6 className="text-uppercase fw-bold text-secondary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <i className="bi-compass me-2"></i>Coordinates
                  </h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">Latitude</small>
                        <p className="mb-0 fw-medium">{selectedCinema.latitude || <span className="text-secondary">—</span>}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">Longitude</small>
                        <p className="mb-0 fw-medium">{selectedCinema.longitude || <span className="text-secondary">—</span>}</p>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Cinema Facilities Section */}
                <div className="mb-4">
                  <h6 className="text-uppercase fw-bold text-secondary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <i className="bi-building me-2"></i>Cinema Facilities
                  </h6>
                  <Row className="g-3">
                    <Col md={4}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">Screens</small>
                        <p className="mb-0 fw-medium text-primary" style={{ fontSize: '1.25rem' }}>{selectedCinema.screens || 0}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">Total Seats</small>
                        <p className="mb-0 fw-medium text-primary" style={{ fontSize: '1.25rem' }}>{selectedCinema.totalSeats || 0}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div style={{ padding: '12px 15px', backgroundColor: '#f8f9fa', borderRadius: '6px' }}>
                        <small className="text-muted d-block mb-1">Vista ID</small>
                        <p className="mb-0 fw-medium">{selectedCinema.vistaCinemaId || selectedCinema.vistacinemaid || <span className="text-secondary">—</span>}</p>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Statistics Section */}
                <div className="mb-4">
                  <h6 className="text-uppercase fw-bold text-secondary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                    <i className="bi-bar-chart me-2"></i>Performance Metrics
                  </h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div style={{ padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '6px', borderLeft: '4px solid #0d6efd' }}>
                        <small className="text-muted d-block mb-1">Total Bookings</small>
                        <p className="mb-0 fw-bold text-primary" style={{ fontSize: '1.5rem' }}>{selectedCinema.totalBookings || 0}</p>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div style={{ padding: '15px', backgroundColor: '#d1e7dd', borderRadius: '6px', borderLeft: '4px solid #198754' }}>
                        <small className="text-muted d-block mb-1">Total Revenue</small>
                        <p className="mb-0 fw-bold text-success" style={{ fontSize: '1.5rem' }}>₹{((selectedCinema.revenue || 0) as number).toLocaleString()}</p>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Amenities Section */}
                {(selectedCinema.amenities && (Array.isArray(selectedCinema.amenities) ? selectedCinema.amenities.length > 0 : selectedCinema.amenities !== '')) && (
                  <div className="mb-2">
                    <h6 className="text-uppercase fw-bold text-secondary mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                      <i className="bi-star me-2"></i>Amenities
                    </h6>
                    <div className="d-flex flex-wrap gap-2">
                      {(Array.isArray(selectedCinema.amenities)
                        ? selectedCinema.amenities
                        : selectedCinema.amenities.split(',').map((a: string) => a.trim()))
                        .map((amenity: string, index: number) => (
                          <span
                            key={index}
                            className="badge bg-light text-dark"
                            style={{ padding: '8px 12px', fontSize: '0.85rem', fontWeight: '500', borderRadius: '20px' }}
                          >
                            <i className="bi-check-circle me-1"></i>{amenity}
                          </span>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-top pt-3">
            <Button
              variant="outline-secondary"
              onClick={() => setShowDetailsModal(false)}
              className="px-4"
            >
              <i className="bi-x-lg me-2"></i>Close
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setShowDetailsModal(false);
                handleOpenEdit(selectedCinema);
              }}
              className="px-4"
            >
              <i className="bi-pencil-square me-2"></i>Edit Cinema
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
};

export default Cinemas;
